import { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { createApi } from '@reduxjs/toolkit/query/react';
import { parse } from 'csv-parse/browser/esm/sync';
import { DateTime } from 'luxon';

import { saveResponseUsingHiddenElement } from 'src/common/util/saveResponseUsingHiddenElement';
import { parseInvoiceExport } from 'src/features/billing/invoices/parseInvoiceExport';
import { bambiBaseQuery } from './bambiBaseQuery';
import type { TripEventRead } from './tripsApi';

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: bambiBaseQuery,
  endpoints: () => ({}),
});

export type InvoiceExportCSVArgOrdering =
  | 'trip__passenger__full_name'
  | 'trip__time_range__startswith'
  | 'trip__payer__display_name';

export type InvoiceExportCSVArg = {
  rangeStart: string;
  rangeEnd?: string;
  invoice?: string;
  tripPayerIn?: string[];
  organizationId?: string;
  outputType?: 'quickbooks' | 'condensed' | 'quickbooks-self-pay';
  format: 'csv' | 'pdf';
  overallPaymentStatusIn?: (
    | 'overpaid'
    | 'paid'
    | 'partially_paid'
    | 'unknown'
    | 'unpaid'
  )[];
  ordering?: InvoiceExportCSVArgOrdering;
};

export type InvoiceSelectedListExportCSVArg = {
  organizationId?: string;
  selectedInvoices: string[];
  format: 'csv' | 'pdf';
  outputType?: 'quickbooks' | 'condensed';
};

export type InvoicePaymentSelectedListExportCSVArg = {
  organizationId?: string;
  selectedInvoices: string[];
  format: 'csv' | 'pdf';
  start_date?: string;
  end_date?: string;
};

export type InvoiceRefundSelectedListExportCSVArg =
  InvoicePaymentSelectedListExportCSVArg;

export type TilledPayoutsExportCSVArg = {
  selectedInvoices?: string;
  paid_at_after?: string;
  paid_at_before?: string;
};

const customTripEndpoints = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // We do not want to cache files in RTKQuery as we could crash the browser
    // The code gen also does not make it possible to parse the response as a blob
    // For more info: https://github.com/reduxjs/redux-toolkit/issues/1522
    getTripsExportAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          payers,
          passengers,
          outputSet,
          organizationId,
          format,
          status,
          driverIn,
          externalTripIds,
          tripIds,
          fields,
        }: {
          rangeStart?: string;
          rangeEnd?: string;
          payers?: string;
          passengers?: string;
          outputSet?: string;
          organizationId?: string;
          format?: string;
          status?: string;
          driverIn?: string;
          externalTripIds?: string;
          tripIds?: string;
          fields?: SelectOption[];
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/trips/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&payers=${payers || ''}&passengers=${passengers || ''}&output_set=${
            outputSet || ''
          }${status ? '&status=' + status : ''}${
            driverIn ? '&driver__in=' + driverIn : ''
          }${
            externalTripIds ? '&external_trip_id__in=' + externalTripIds : ''
          }${tripIds ? '&id__in=' + tripIds : ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => {
            if (format && format === 'pdf') {
              return response.text();
            }
            return response.blob();
          },
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `trip_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        // PDF exports are handled separately
        if (format && format === 'pdf') {
          return {
            data: {
              records: result.data,
              fileName,
            },
          };
        }

        let finalResult = result;

        if (fields) {
          const data = await (result.data as Blob).text();
          const records: Record<string, string>[] = parse(data, {
            columns: true,
          });

          const filtered = records.map((record) => {
            const copy: Record<string, string> = {};
            for (const field of fields) {
              copy[field.value] = record[field.value];
            }
            return copy;
          });

          const headers = Object.keys(filtered[0] ?? {});
          const csvRows = [
            fields.map((f) => f.label).join(','),
            ...filtered.map((row) =>
              headers.map((h) => JSON.stringify(row[h] ?? '')).join(',')
            ),
          ];

          finalResult.data = new Blob([csvRows.join('\n')], {
            type: 'text/csv',
          });
        }

        saveResponseUsingHiddenElement(finalResult, fileName);
        return { data: null };
      },
    }),
    getTripsExportAsTxt: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          payers,
          passengers,
          outputSet,
          organizationId,
          format,
          status,
          driverIn,
          externalTripIds,
          tripIds,
        }: {
          rangeStart?: string;
          rangeEnd?: string;
          payers?: string;
          passengers?: string;
          outputSet?: string;
          organizationId?: string;
          format?: string;
          status?: string;
          driverIn?: string;
          externalTripIds?: string;
          tripIds?: string;
        },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/trips/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&payers=${payers || ''}&passengers=${passengers || ''}&output_set=${
            outputSet || ''
          }${status ? '&status=' + status : ''}${
            driverIn ? '&driver__in=' + driverIn : ''
          }${
            externalTripIds ? '&external_trip_id__in=' + externalTripIds : ''
          }${tripIds ? '&id__in=' + tripIds : ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => {
            if (format && format === 'pdf') {
              return response.text();
            }
            return response.blob();
          },
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `trip_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.txt`;
        const fileName = getFilename(responseHeaders, fallback);

        // PDF exports are handled separately
        if (format && format === 'pdf') {
          return {
            data: {
              records: result.data,
              fileName,
            },
          };
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getAssignmentExportAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        { rangeStart, rangeEnd, outputSet, organizationId },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/assignments/export/?range_start=${
            rangeStart || ''
          }&range_end=${rangeEnd || ''}&output_set=${outputSet || ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => response.blob(),
        });

        if (result.error) {
          return {
            error: {
              status:
                typeof result.error.status === 'number'
                  ? result.error.status
                  : 500,
              statusText: 'Error downloading file',
              data: null,
            },
          };
        }

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `assignment_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        saveResponseUsingHiddenElement(result, fileName);
        return { data: 'Download started' };
      },
    }),
    getInvoicesExportListAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          rangeStart,
          rangeEnd,
          tripPayerIn,
          invoice,
          overallPaymentStatusIn,
          organizationId,
          outputType,
          format = 'csv',
          ordering,
        }: InvoiceExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export/?range_start=${rangeStart || ''}&range_end=${
            rangeEnd || ''
          }&trip__payer__in=${tripPayerIn || ''}&invoice=${
            invoice || ''
          }&output_type=${
            outputType ?? 'quickbooks'
          }&overall_payment_status__in=${
            overallPaymentStatusIn?.join(',') ?? ''
          }&ordering=${ordering ?? ''}`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          outputType,
          format = 'csv',
        }: InvoiceSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export/list-selected/?output_type=${
            outputType ?? 'quickbooks'
          }`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesPaymentsExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          format = 'csv',
          start_date,
          end_date,
        }: InvoicePaymentSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export-payments/list-selected/`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
            start_date,
            end_date,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_payments_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getInvoicesRefundsExportListBySelectedInvoiceCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          organizationId,
          selectedInvoices,
          format = 'csv',
        }: InvoiceRefundSelectedListExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/invoices/export-refunds/list-selected/`,
          method: 'POST',
          headers: {
            'organization-id': organizationId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selected_invoices: selectedInvoices,
          }),
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `invoice_payments_list_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        if (format !== 'csv') {
          const records = await (result.data as Blob)?.text();
          return parseInvoiceExport(records, fileName);
        }

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getVehicleInspectionsExportListAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        { rangeStart, rangeEnd },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const now = DateTime.now().toFormat('MMddyyyyHHmmss');
        const encodedRangeStart = encodeURIComponent(rangeStart || '');
        const encodedRangeEnd = encodeURIComponent(rangeEnd || '');
        const params = `range_start=${encodedRangeStart}&range_end=${encodedRangeEnd}`;
        const result = await baseQuery({
          url: `/vehicles/inspections/export/?${params}`,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        });

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `vehicle_inspections_export_${now}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getBulkTripEvents: builder.query<TripEventRead[], { trip_ids: string[] }>({
      queryFn: async ({ trip_ids }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/trips/bulk-trip-events/`,
          method: 'POST',
          body: JSON.stringify({ trip_ids }),
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return {
          data: result.data as TripEventRead[], // Type assertion to ensure correct response type
        };
      },
    }),
    getPassengersExportAsCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async ({ organizationId }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/passengers/export/`,
          method: 'GET',
          headers: {
            'organization-id': organizationId,
          },
          responseHandler: (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `passengers_export_${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
    getTilledPayoutsExportCsv: builder.query({
      keepUnusedDataFor: 0,
      queryFn: async (
        {
          selectedInvoices,
          paid_at_after,
          paid_at_before,
        }: TilledPayoutsExportCSVArg,
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/tilled/payouts/export/`,
          method: 'POST',
          body: {},
          params: {
            paid_at_after,
            paid_at_before,
            selected_invoices: selectedInvoices,
          },
          responseHandler: (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        // @ts-ignore
        const responseHeaders: Headers = result.meta?.response?.headers;
        const fallback = `tilled-payouts-export-${DateTime.now().toFormat(
          'MMddyyyyHHmmss'
        )}.csv`;
        const fileName = getFilename(responseHeaders, fallback);

        saveResponseUsingHiddenElement(result, fileName);
        return { data: null };
      },
    }),
  }),
});

function getFilename(headers: Headers, fallback: string): string {
  const content = headers?.get('content-disposition');
  if (!content) {
    return fallback;
  }

  const filename = content.match(/filename="(.*)?"/);
  if (!filename || !filename[1]) {
    return fallback;
  }

  return filename[1];
}

export const {
  useLazyGetTripsExportAsCsvQuery,
  useLazyGetTripsExportAsTxtQuery,
  useLazyGetAssignmentExportAsCsvQuery,
  useLazyGetInvoicesExportListAsCsvQuery,
  useLazyGetVehicleInspectionsExportListAsCsvQuery,
  useLazyGetBulkTripEventsQuery,
  useLazyGetInvoicesExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesPaymentsExportListBySelectedInvoiceCsvQuery,
  useLazyGetInvoicesRefundsExportListBySelectedInvoiceCsvQuery,
  useLazyGetPassengersExportAsCsvQuery,
  useLazyGetTilledPayoutsExportCsvQuery,
} = customTripEndpoints;
